import { useRef, useState } from "react";
import { FileText, Upload, X } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/form";
import { UseFormReturn } from "react-hook-form";
import { MAX_FILE_SIZE_BYTES, getFileSizeErrorMessage } from "@/shared/lib/fileUploadConstants";
import { toast } from "@/shared/widgets/toast";
import { DocumentFormValues, ACCEPTED_DOCUMENT_TYPES } from "./utils";

interface DocumentFileUploadProps {
  form: UseFormReturn<DocumentFormValues>;
  fileInputKey: number;
  onFileInputKeyChange: () => void;
  disabled?: boolean;
  label?: string;
  description?: string;
  currentFileName?: string;
}

export function DocumentFileUpload({
  form,
  fileInputKey,
  onFileInputKeyChange,
  disabled = false,
  label = "Arquivo (opcional)",
  description = "Envie um arquivo ou informe a URL. Pelo menos um dos dois é obrigatório.",
  currentFileName,
}: DocumentFileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedFile = form.watch("file");
  const hasSelectedFile = selectedFile instanceof File;
  const inputId = `receivable-document-upload-${fileInputKey}`;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.api.error("Arquivo muito grande", getFileSizeErrorMessage());
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
    }
    form.setValue("file", file ?? undefined, { shouldDirty: true, shouldValidate: true });
  };

  const handleRemoveFile = () => {
    form.setValue("file", undefined, { shouldDirty: true, shouldValidate: true });
    onFileInputKeyChange();
  };

  return (
    <FormField
      control={form.control}
      name="file"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <div className="space-y-4">
              {(hasSelectedFile || currentFileName) && (
                <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-4">
                  <FileText className="h-5 w-5 text-primary" />
                  <div className="min-w-0 flex-1">
                    <p className="break-all text-sm font-medium">
                      {hasSelectedFile ? selectedFile.name : currentFileName}
                    </p>
                    {hasSelectedFile && (
                      <p className="text-xs text-muted-foreground">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={handleRemoveFile}
                    disabled={disabled}
                    title="Remover arquivo selecionado"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}

              <label
                htmlFor={inputId}
                className={`flex cursor-pointer flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed p-6 text-center transition-colors ${
                  disabled
                    ? "pointer-events-none opacity-60"
                    : "hover:border-neutral-600 hover:bg-neutral-600/5"
                }`}
              >
                <Upload className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">
                    {hasSelectedFile ? "Trocar arquivo" : "Clique para enviar arquivo"}
                  </p>
                  <p className="text-xs text-muted-foreground">PDF, imagens ou DOC (até 500KB)</p>
                </div>
              </label>
              <Input
                key={fileInputKey}
                id={inputId}
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_DOCUMENT_TYPES}
                onChange={handleFileChange}
                onBlur={field.onBlur}
                className="hidden"
                disabled={disabled}
              />
            </div>
          </FormControl>
          <FormDescription>{description}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

